import React, { createContext, useState, useEffect, useCallback } from "react";

// Create UserContext
const UserContext = createContext();

// Session expiration time: 24 hours in milliseconds
const SESSION_EXPIRATION_TIME = 24 * 60 * 60 * 1000;

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);

  // Check if session has expired
  const checkSessionExpiration = useCallback(() => {
    const lastActivityTime = localStorage.getItem("lastActivityTime");
    if (lastActivityTime) {
      const timeSinceLastActivity = Date.now() - parseInt(lastActivityTime, 10);
      if (timeSinceLastActivity > SESSION_EXPIRATION_TIME) {
        // Session expired - auto logout
        logoutUser();
        return true;
      }
    }
    return false;
  }, []);

  // Update last activity time
  const updateActivity = useCallback(() => {
    if (user) {
      localStorage.setItem("lastActivityTime", Date.now().toString());
    }
  }, [user]);

  const loginUser = (userData) => {
    // userData should include: username, role ('EMPLOYEE', 'ADMIN', or regular user data)
    setUser(userData);
    localStorage.setItem("user", JSON.stringify(userData));
    localStorage.setItem("isLoggedIn", "true");
    localStorage.setItem("lastActivityTime", Date.now().toString());
  };

  const logoutUser = () => {
    setUser(null);
    localStorage.removeItem("user");
    localStorage.removeItem("isLoggedIn");
    localStorage.removeItem("lastActivityTime");
    // Only redirect if we're not already on the home page
    if (window.location.pathname !== "/") {
      window.location.href = "/";
    }
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem("user", JSON.stringify(updatedUser));
    updateActivity(); // Update activity on user update
  };

  // Check session on mount and restore user if valid
  useEffect(() => {
    const loggedIn = localStorage.getItem("isLoggedIn") === "true";
    const storedUser = localStorage.getItem("user");

    if (loggedIn && storedUser) {
      // Check if session is still valid
      const isExpired = checkSessionExpiration();
      if (!isExpired) {
        setUser(JSON.parse(storedUser));
        updateActivity(); // Update activity time on page load
      }
    }
  }, [checkSessionExpiration]);

  // Track user activity (mouse moves, clicks, keyboard input)
  useEffect(() => {
    if (!user) return;

    let activityTimeout;

    const handleActivity = () => {
      // Debounce activity updates to avoid too many localStorage writes
      clearTimeout(activityTimeout);
      activityTimeout = setTimeout(() => {
        updateActivity();
      }, 60000); // Update every minute of activity
    };

    // Listen for user activity
    window.addEventListener("mousemove", handleActivity);
    window.addEventListener("mousedown", handleActivity);
    window.addEventListener("keypress", handleActivity);
    window.addEventListener("scroll", handleActivity);
    window.addEventListener("touchstart", handleActivity);

    // Check session expiration every 5 minutes
    const expirationCheckInterval = setInterval(
      () => {
        checkSessionExpiration();
      },
      5 * 60 * 1000,
    );

    return () => {
      clearTimeout(activityTimeout);
      window.removeEventListener("mousemove", handleActivity);
      window.removeEventListener("mousedown", handleActivity);
      window.removeEventListener("keypress", handleActivity);
      window.removeEventListener("scroll", handleActivity);
      window.removeEventListener("touchstart", handleActivity);
      clearInterval(expirationCheckInterval);
    };
  }, [user, updateActivity, checkSessionExpiration]);

  return (
    <UserContext.Provider
      value={{ user, loginUser, logoutUser, updateUser, updateActivity }}
    >
      {children}
    </UserContext.Provider>
  );
};

export default UserContext;
